import React, { useEffect, useState, useCallback } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { TrendingUp, Cloud, ShieldCheck, Layers, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  cyan: '#06B6D4',
  purple: '#A78BFA',
  green: '#10B981',
  amber: '#F59E0B',
  red: '#EF4444',
  pink: '#EC4899',
  indigo: '#6366F1',
  teal: '#14B8A6',
};
const PIE_COLORS = [COLORS.cyan, COLORS.purple, COLORS.green, COLORS.amber, COLORS.red, COLORS.pink, COLORS.indigo, COLORS.teal];

const Card = ({ title, icon: Icon, accent = 'cyan', children }) => {
  const accentColor = COLORS[accent] || COLORS.cyan;
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
      <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2 uppercase tracking-wider">
        {Icon && <Icon className="w-4 h-4" style={{ color: accentColor }} />}
        {title}
      </h3>
      {children}
    </div>
  );
};

const dailySignupSeries = (daily, days = 30) => {
  const out = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    out.push({ date: key.slice(5), signups: daily?.[key] || 0 });
  }
  return out;
};

const auditDailySeries = (auditsPerDay, days = 14) => {
  const out = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const bucket = auditsPerDay?.[key] || { passed: 0, failed: 0 };
    out.push({ date: key.slice(5), passed: bucket.passed, failed: bucket.failed });
  }
  return out;
};

export const AdminAnalytics = ({ dashboard, getAuthHeaders, onAuthError }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/analytics`, { headers: getAuthHeaders() });
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) { onAuthError && onAuthError(); return; }
        throw new Error('Failed to load analytics');
      }
      setAnalytics(await res.json());
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, onAuthError]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="admin-analytics-loading">
        <RefreshCw className="w-6 h-6 text-cyan-400 animate-spin" />
      </div>
    );
  }
  if (error) {
    return <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm" data-testid="admin-analytics-error">{error}</div>;
  }

  const signupSeries = dailySignupSeries(dashboard?.daily_signups);
  const subsData = [
    { name: 'Free', value: dashboard?.subscriptions?.free || 0 },
    { name: 'Growth', value: dashboard?.subscriptions?.growth || 0 },
    { name: 'Enterprise', value: dashboard?.subscriptions?.enterprise || 0 },
  ];
  const phaseData = [
    { name: 'Phase 1', value: dashboard?.projects_by_phase?.phase1 || 0 },
    { name: 'Phase 2', value: dashboard?.projects_by_phase?.phase2 || 0 },
    { name: 'Phase 3', value: dashboard?.projects_by_phase?.phase3 || 0 },
    { name: 'Phase 4', value: dashboard?.projects_by_phase?.phase4 || 0 },
  ];
  const cloudData = Object.entries(analytics?.cloud_breakdown || {}).map(([name, value]) => ({ name, value }));
  const auditDaily = auditDailySeries(analytics?.audits_per_day);
  const passRate = analytics?.audit_summary?.pass_rate_pct ?? 0;
  const frameworks = analytics?.top_frameworks || [];

  return (
    <div className="space-y-6" data-testid="admin-analytics-pane">
      {/* Top KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card title="Audit Pass Rate" icon={ShieldCheck} accent="green">
          <div className="flex items-end justify-between">
            <span className="text-4xl font-bold text-white" data-testid="admin-pass-rate">{passRate}%</span>
            <span className="text-xs text-zinc-500">{analytics?.audit_summary?.passed || 0} passed / {analytics?.audit_summary?.total || 0} total</span>
          </div>
        </Card>
        <Card title="Top Cloud" icon={Cloud} accent="cyan">
          <span className="text-4xl font-bold text-white">{cloudData[0]?.name || '—'}</span>
          <span className="block text-xs text-zinc-500 mt-1">{cloudData[0]?.value || 0} projects</span>
        </Card>
        <Card title="Top Framework" icon={Layers} accent="purple">
          <span className="text-4xl font-bold text-white">{frameworks[0]?.name || '—'}</span>
          <span className="block text-xs text-zinc-500 mt-1">{frameworks[0]?.count || 0} projects</span>
        </Card>
      </div>

      {/* Daily Signups area chart */}
      <Card title="Daily Signups (Last 30 Days)" icon={TrendingUp} accent="cyan">
        <div className="h-64" data-testid="admin-chart-signups">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={signupSeries} margin={{ top: 6, right: 12, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="gSig" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={COLORS.cyan} stopOpacity={0.8} />
                  <stop offset="100%" stopColor={COLORS.cyan} stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="date" stroke="#71717a" fontSize={11} />
              <YAxis allowDecimals={false} stroke="#71717a" fontSize={11} />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8, fontSize: 12 }} />
              <Area type="monotone" dataKey="signups" stroke={COLORS.cyan} fill="url(#gSig)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Two-up: Subscription mix + Projects by phase (donut + bar) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Subscription Mix" icon={Layers} accent="purple">
          <div className="h-60" data-testid="admin-chart-subs">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={subsData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={88} paddingAngle={2}>
                  {subsData.map((entry, i) => <Cell key={i} fill={[COLORS.cyan, COLORS.purple, COLORS.amber][i]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12, color: '#a1a1aa' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Projects by Phase" icon={Layers} accent="cyan">
          <div className="h-60" data-testid="admin-chart-phases">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={phaseData} margin={{ top: 6, right: 12, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="name" stroke="#71717a" fontSize={11} />
                <YAxis allowDecimals={false} stroke="#71717a" fontSize={11} />
                <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="value" fill={COLORS.cyan} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Cloud platform + Top frameworks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Cloud Platform Breakdown" icon={Cloud} accent="cyan">
          <div className="h-60" data-testid="admin-chart-clouds">
            {cloudData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-zinc-500 text-sm">No projects yet</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cloudData} layout="vertical" margin={{ top: 6, right: 12, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis type="number" allowDecimals={false} stroke="#71717a" fontSize={11} />
                  <YAxis type="category" dataKey="name" stroke="#71717a" fontSize={11} width={84} />
                  <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                    {cloudData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
        <Card title="Top Compliance Frameworks" icon={ShieldCheck} accent="green">
          <div className="space-y-2.5" data-testid="admin-top-frameworks">
            {frameworks.length === 0 && <div className="text-sm text-zinc-500">No frameworks selected yet.</div>}
            {frameworks.map((f, i) => {
              const max = frameworks[0]?.count || 1;
              const pct = (f.count / max) * 100;
              return (
                <div key={f.name} className="flex items-center gap-3">
                  <span className="text-xs text-zinc-300 w-28 truncate">{f.name}</span>
                  <div className="flex-1 h-2 rounded-full bg-zinc-800 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  </div>
                  <span className="text-xs font-mono text-zinc-400 w-8 text-right">{f.count}</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Audit pass/fail per day */}
      <Card title="Audits Per Day (Last 14 Days) — pass vs fail" icon={ShieldCheck} accent="green">
        <div className="h-60" data-testid="admin-chart-audits">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={auditDaily} margin={{ top: 6, right: 12, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="date" stroke="#71717a" fontSize={11} />
              <YAxis allowDecimals={false} stroke="#71717a" fontSize={11} />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12, color: '#a1a1aa' }} />
              <Bar dataKey="passed" stackId="a" fill={COLORS.green} radius={[6, 6, 0, 0]} />
              <Bar dataKey="failed" stackId="a" fill={COLORS.red} radius={[0, 0, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
};
